import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { Bell } from 'lucide-react';
import { api } from '../../convex/_generated/api';

interface NotificationBellProps {
  role: 'parent' | 'coach' | 'director';
  className?: string;
}

function useAnnouncementsForRole(role: 'parent' | 'coach' | 'director') {
  const parentData = useQuery(
    api.parent.announcements.list,
    role === 'parent' ? { limit: 20 } : 'skip'
  );
  const coachData = useQuery(
    api.coach.announcements.list,
    role === 'coach' ? { limit: 20 } : 'skip'
  );
  const directorData = useQuery(
    api.director.announcements.list,
    role === 'director' ? { limit: 20 } : 'skip'
  );

  if (role === 'parent') return parentData;
  if (role === 'coach') return coachData;
  return directorData;
}

function useDmUnreadCount(role: 'parent' | 'coach' | 'director') {
  const parentDms = useQuery(
    api.parent.messages.unreadCount,
    role === 'parent' ? {} : 'skip'
  );
  const coachDms = useQuery(
    api.coach.messages.unreadCount,
    role === 'coach' ? {} : 'skip'
  );
  if (role === 'parent') return (parentDms as number) ?? 0;
  if (role === 'coach') return (coachDms as number) ?? 0;
  return 0;
}

export function NotificationBell({ role, className = 'text-white hover:bg-white/20' }: NotificationBellProps) {
  const navigate = useNavigate();
  const storageKey = `${role}-messages-last-seen`;
  const data = useAnnouncementsForRole(role);
  const dmUnread = useDmUnreadCount(role);

  const [lastSeenTs, setLastSeenTs] = useState<number>(0);

  // Read lastSeen from localStorage on mount and on window focus
  useEffect(() => {
    function readLastSeen() {
      const lastSeen = localStorage.getItem(storageKey);
      setLastSeenTs(lastSeen ? new Date(lastSeen).getTime() : 0);
    }
    readLastSeen();

    window.addEventListener('focus', readLastSeen);
    return () => window.removeEventListener('focus', readLastSeen);
  }, [storageKey]);

  const announcementUnread = useMemo(() => {
    if (!data?.announcements) return 0;
    return data.announcements.filter(
      (a: { createdAt: number }) => a.createdAt > lastSeenTs
    ).length;
  }, [data, lastSeenTs]);

  const totalUnread = announcementUnread + dmUnread;

  const announcementsPath: Record<typeof role, string> = {
    parent: '/parent/announcements',
    coach: '/coach/announcements',
    director: '/director/announcements',
  };

  const handleClick = () => {
    localStorage.setItem(storageKey, new Date().toISOString());
    setLastSeenTs(Date.now());
    navigate(announcementsPath[role]);
  };

  return (
    <button
      onClick={handleClick}
      className={`relative p-2 rounded-lg transition ${className}`}
    >
      <Bell className="w-6 h-6" />
      {totalUnread > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1">
          {totalUnread > 99 ? '99+' : totalUnread}
        </span>
      )}
    </button>
  );
}

// Hook to mark messages as seen (call this on messages page)
export function useMarkMessagesSeen(role: 'parent' | 'coach' | 'director') {
  useEffect(() => {
    localStorage.setItem(`${role}-messages-last-seen`, new Date().toISOString());
  }, [role]);
}
